<template>
  <div class="new-report-container bg-white pb-120px">
    <NewReportHeader @autofill="autofillForm" />

    <div class="pt-40px">
      <StepIndicator :steps="steps" />

      <NewReportForm ref="reportForm" @submit-form="submitForm" />
    </div>

    <NavigationControls :total-steps="steps.length" @next-step="nextStep" @submit="submitForm" />

    <LoadingModal
      v-if="showGeneratingDialog"
      v-model="showGeneratingDialog"
      :report-id="reportId"
      :report-name="store.formData.project_name"
      :on-complete="handleReportComplete"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { showToast } from "@/utilities/toast";
import ReportsAPI from "@/api/reports";
import { useReportFormStore } from "@/stores/reportFormStore";
import { sampleAutofillData } from "@/utilities/collections/newReportOptions";

import LoadingModal from "@/views/NewReportView/components/LoadingModal.vue";
import StepIndicator from "@/views/NewReportView/components/StepIndicator.vue";
import NewReportHeader from "@/views/NewReportView/components/NewReportHeader.vue";
import NavigationControls from "@/views/NewReportView/components/NavigationControls.vue";
import NewReportForm from "@/views/NewReportView/components/NewReportForm.vue";

const router = useRouter();
const store = useReportFormStore();

// Reset form when navigating to this page
onMounted(() => {
  store.resetForm();
});

const steps = [
  { name: "User Information" },
  { name: "Project information & site data" },
  { name: "Pre-development" },
  { name: "Post-development" },
  { name: "Design storm frequency" },
];
const reportForm = ref(null);

const showGeneratingDialog = ref(false);
const reportId = ref("");
const isSubmitting = ref(false);
const showTimeoutError = ref(false);
const generationTimeout = ref(null);

// Step navigation methods
const nextStep = async () => {
  const valid = await reportForm.value.validateCurrentStep();
  if (valid) {
    store.nextStep();
  }
};

const submitForm = async () => {
  try {
    isSubmitting.value = true;
    reportId.value = "";

    // Get API-ready data from the store
    const apiData = store.getApiFormData();

    // Ensure property_boundary is a string before sending to backend
    if (apiData.property_boundary && typeof apiData.property_boundary !== "string") {
      apiData.property_boundary = JSON.stringify(apiData.property_boundary);
    }

    // Make the API call to create the report
    const response = await ReportsAPI.createReport(apiData);

    // Check for ID in the response (response could have different structure)
    if (response && response.id) {
      reportId.value = String(response.id);

      // Now show the generating dialog with the valid report ID
      showGeneratingDialog.value = true;

      // Set a timeout to prevent the app from getting stuck (3 minutes for longer reports)
      const timeout = setTimeout(() => {
        if (showGeneratingDialog.value) {
          reportId.value = "timeout";
          showGeneratingDialog.value = false;
          showTimeoutError.value = true;
        }
      }, 180000);

      // Store the timeout so we can clear it if needed
      generationTimeout.value = timeout;
    } else {
      console.error("API response format unexpected:", response);
      showToast("Failed to create report: Missing report ID", { type: "error" });
    }
  } catch (error) {
    console.error("Error submitting form:", error);
    showToast("An error occurred while creating the report", { type: "error" });
  } finally {
    isSubmitting.value = false;
  }
};

const handleReportComplete = () => {
  try {
    // Clear any timeout we might have set
    if (generationTimeout.value) {
      clearTimeout(generationTimeout.value);
      generationTimeout.value = null;
    }

    // Hide the dialog first
    showGeneratingDialog.value = false;

    // Reset the form
    store.resetForm();

    // Validate reportId before navigation
    if (!reportId.value || reportId.value === "timeout" || reportId.value === "") {
      console.error("Invalid report ID for navigation:", reportId.value);
      showToast("Report was generated but has an invalid ID. Please go to the Reports page.", { type: "warning" });
      setTimeout(() => {
        router.push({ name: "reports" });
      }, 1500);
      return;
    }

    // Then navigate with a small delay
    setTimeout(() => {
      router.push({ name: "report-details", params: { id: reportId.value } });
    }, 500);
  } catch (error) {
    console.error("Error navigating to report:", error);
    showToast("Report was generated but navigation failed. Please go to the Reports page.", { type: "warning" });

    // Try to navigate to the reports list as a fallback
    setTimeout(() => {
      router.push({ name: "reports" });
    }, 1500);
  }
};

// Add autofill function
const autofillForm = async () => {
  // Pass the sample data to the form component
  reportForm.value.autofill(sampleAutofillData);

  // Show success message
  showToast("Form has been autofilled with sample data", { type: "success" });
};
</script>

<style lang="scss" scoped>
.new-report-container {
  height: 100%;
  min-height: 100vh;
  position: relative;
}

:deep(.el-form) {
  margin: 0 auto;
}
</style>
